(() => {
  'use strict';

  if (window.__awcCroAnalyticsReady) return;
  window.__awcCroAnalyticsReady = true;

  const emitted = new Set();
  const GA4_MEASUREMENT_ID = 'G-3RTVSQ2PGE';
  let addToCartSource = 'main';
  let pendingOptionFocus = null;

  const clean = (value) => String(value == null ? '' : value).trim();
  const compact = (value, limit = 100) => clean(value).replace(/\s+/g, ' ').slice(0, limit);
  const number = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  };
  const deviceType = () => window.matchMedia('(max-width: 768px)').matches ? 'mobile' : 'desktop';
  const pageType = () => {
    if (document.querySelector('[data-product-section]')) return 'product';
    if (document.querySelector('[data-collection-page], .collection-page')) return 'collection';
    if (document.querySelector('[data-cart-page]')) return 'cart';
    if (location.pathname.startsWith('/search')) return 'search';
    if (location.pathname === '/') return 'home';
    if (location.pathname.startsWith('/pages/')) return 'page';
    if (location.pathname.startsWith('/blogs/')) return 'blog';
    return 'other';
  };

  function analyticsValue(value) {
    if (value == null) return '';
    if (typeof value === 'string') return compact(value);
    if (typeof value === 'number' || typeof value === 'boolean') return value;
    try { return compact(JSON.stringify(value)); } catch (error) { return compact(value); }
  }

  function publish(name, parameters = {}, onceKey = '') {
    if (onceKey && emitted.has(onceKey)) return;
    if (onceKey) emitted.add(onceKey);

    const rawPayload = Object.assign({
      send_to: GA4_MEASUREMENT_ID,
      device_type: deviceType(),
      page_type: pageType(),
      page_path: location.pathname,
      page_title: compact(document.title)
    }, parameters);
    const payload = {};
    Object.entries(rawPayload).forEach(([key, value]) => {
      payload[key] = analyticsValue(value);
    });
    window.dataLayer = window.dataLayer || [];
    if (typeof window.gtag === 'function') {
      window.gtag('event', name, payload);
    } else {
      window.dataLayer.push(Object.assign({ event: name }, payload));
    }

    try {
      if (window.Shopify?.analytics?.publish) {
        Promise.resolve(window.Shopify.analytics.publish(name, payload)).catch(() => {});
      }
    } catch (error) {}

    document.dispatchEvent(new CustomEvent(`cro:${name}`, { detail: payload }));
  }

  window.AWCCRO = { publish };

  function productPayload(root, extra = {}) {
    const variant = root ? selectedVariant(root) : {};
    const configuration = root ? productConfiguration(root) : {};
    return Object.assign({
      item_id: clean(root?.dataset.productId),
      item_name: clean(root?.dataset.productTitle),
      product_type: clean(root?.dataset.productTypeName),
      product_category: clean(root?.dataset.productCategoryName),
      variant_id: clean(variant?.id || root?.dataset.variantId),
      variant_name: clean(variant?.title),
      visible_price: number(variant?.price || root?.dataset.productPrice),
      currency: clean(root?.dataset.currency) || 'GBP',
      selected_configuration: Object.entries(configuration).map(([key, value]) => `${key}: ${value}`).join(' | ')
    }, extra);
  }

  function visiblePosition(element, selector) {
    if (!element) return 0;
    const scope = element.closest('.variant-panel, [data-fabric-panel], .gallery-thumbs, .reviews-list, .pdp, [data-product-section]') || document;
    const items = Array.from(scope.querySelectorAll(selector)).filter((item) => {
      if (item.hidden || item.disabled) return false;
      const styles = getComputedStyle(item);
      return styles.display !== 'none' && styles.visibility !== 'hidden';
    });
    return items.indexOf(element) + 1;
  }

  function fabricGroup(button) {
    const panel = button?.closest('[data-fabric-panel]');
    const heading = panel?.querySelector('.fabric-panel-heading, h3, [aria-label]');
    return clean(panel?.dataset.fabricPanel || heading?.textContent);
  }

  function swatchImage(button) {
    const image = button?.querySelector('img');
    return clean(image?.dataset.fabricSwatchSrc || image?.currentSrc || image?.src);
  }

  function selectedVariant(root) {
    const id = clean(root.querySelector('[data-product-form] input[name="id"]')?.value || root.dataset.variantId);
    const json = root.querySelector('[data-product-json]');
    if (!json) return { id, price: number(root.dataset.productPrice), options: [] };
    try {
      const variants = JSON.parse(json.textContent);
      return variants.find((variant) => String(variant.id) === id) || { id, price: number(root.dataset.productPrice), options: [] };
    } catch (error) {
      return { id, price: number(root.dataset.productPrice), options: [] };
    }
  }

  function productConfiguration(root) {
    const configuration = {};
    root.querySelectorAll('[data-variant-tab]').forEach((tab) => {
      const name = clean(tab.querySelector('.variant-tab-label')?.textContent);
      const value = clean(tab.querySelector('[data-tab-current], [data-storage-tab-current]')?.textContent);
      if (name && value) configuration[name] = value;
    });
    return configuration;
  }

  function optionName(target) {
    const panel = target.closest('.variant-panel');
    const labelledBy = panel?.getAttribute('aria-labelledby');
    const label = labelledBy ? document.getElementById(labelledBy)?.querySelector('.variant-tab-label') : null;
    return clean(label?.textContent) || `Option ${number(target.dataset.optionIndex) + 1}`;
  }

  function announce(root, message) {
    let live = root.querySelector('[data-cro-live]');
    if (!live) {
      live = document.createElement('div');
      live.className = 'sr-only';
      live.dataset.croLive = '';
      live.setAttribute('role', 'status');
      live.setAttribute('aria-live', 'polite');
      live.setAttribute('aria-atomic', 'true');
      root.appendChild(live);
    }
    live.textContent = '';
    window.setTimeout(() => { live.textContent = message; }, 30);
  }

  function rememberOptionFocus(option) {
    if (!option) return;
    pendingOptionFocus = {
      root: option.closest('[data-product-section]'),
      index: clean(option.dataset.optionIndex),
      value: clean(option.dataset.optionValue),
      startedAt: Date.now()
    };
  }

  function restoreOptionFocus() {
    if (!pendingOptionFocus || Date.now() - pendingOptionFocus.startedAt > 1600) return;
    const active = document.activeElement;
    if (active && active !== document.body && active !== document.documentElement && active.isConnected) return;
    const root = pendingOptionFocus.root?.isConnected
      ? pendingOptionFocus.root
      : document.querySelector('[data-product-section]');
    if (!root) return;
    const match = Array.from(root.querySelectorAll('[data-option-index][data-option-value]')).find((option) => (
      clean(option.dataset.optionIndex) === pendingOptionFocus.index
      && clean(option.dataset.optionValue) === pendingOptionFocus.value
      && !option.disabled
    ));
    if (match) match.focus({ preventScroll: true });
  }

  function protectStickyFocus() {
    const sticky = document.querySelector('[data-sticky-atc]');
    const mainButton = document.querySelector('[data-add-to-cart]');
    if (!sticky || !mainButton) return;
    const observer = new MutationObserver(() => {
      if (!sticky.classList.contains('is-visible') && sticky.contains(document.activeElement)) {
        mainButton.focus({ preventScroll: true });
      }
    });
    observer.observe(sticky, { attributes: true, attributeFilter: ['class'] });
  }

  function initPageViews() {
    const productRoot = document.querySelector('[data-product-section]');
    if (productRoot) {
      const variant = selectedVariant(productRoot);
      productRoot.dataset.variantId = variant.id || productRoot.dataset.variantId || '';
      productRoot.dataset.productPrice = String(variant.price || productRoot.dataset.productPrice || 0);
      publish('cro_product_view', {
        product_id: clean(productRoot.dataset.productId),
        product_name: clean(productRoot.dataset.productTitle),
        product_url: clean(productRoot.dataset.productUrl),
        variant_id: clean(variant.id),
        variant_name: clean(variant.title),
        price: number(variant.price),
        currency: clean(productRoot.dataset.currency) || 'GBP'
      }, `view_item:${productRoot.dataset.productId}:${variant.id}`);
      publish('product_view', productPayload(productRoot, {
        event_label: `Viewed product: ${clean(productRoot.dataset.productTitle)}`,
        product_url: clean(productRoot.dataset.productUrl)
      }), `ga4_product_view:${productRoot.dataset.productId}:${variant.id}`);
    }

    const cartRoot = document.querySelector('[data-cart-page]');
    if (cartRoot) {
      const hasServices = Boolean(cartRoot.querySelector('[data-service-option][aria-pressed="true"], [data-bundle-role="service"]'));
      publish('cro_cart_view', {
        item_count: number(cartRoot.dataset.cartItemCount),
        cart_total: number(cartRoot.dataset.cartTotal),
        discount_state: clean(cartRoot.dataset.cartDiscountState) || 'none',
        services_state: hasServices ? 'selected' : 'none'
      }, `view_cart:${location.pathname}:${performance.timeOrigin}`);
    }
  }

  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;
    const productRoot = event.target.closest('[data-product-section]');
    if (productRoot) {
      const tab = event.target.closest('[data-variant-tab]');
      if (tab) {
        const tabName = clean(tab.querySelector('.variant-tab-label')?.textContent || tab.textContent);
        const tabValue = clean(tab.querySelector('[data-tab-current], [data-storage-tab-current]')?.textContent);
        publish('product_option_tab_click', productPayload(productRoot, {
          event_label: `Opened option tab: ${tabName}${tabValue ? ` (${tabValue})` : ''}`,
          option_name: tabName,
          option_value: tabValue,
          element_position: visiblePosition(tab, '[data-variant-tab]')
        }));
      }

      const colour = event.target.closest('.color-opt[data-option-index][data-option-value], [data-color-option][data-option-value]');
      if (colour && !colour.disabled) {
        const colourName = clean(colour.dataset.optionValue);
        const previousColour = clean(productRoot.querySelector('[data-product-color-current]')?.textContent);
        publish('product_colour_swatch_click', productPayload(productRoot, {
          event_label: `Colour swatch clicked: ${colourName}`,
          option_name: optionName(colour),
          colour_name: colourName,
          previous_colour: previousColour,
          fabric_group: fabricGroup(colour),
          colour_position: visiblePosition(colour, '.color-opt[data-option-index][data-option-value], [data-color-option][data-option-value]'),
          swatch_image: swatchImage(colour)
        }));
      }

      const galleryControl = event.target.closest('[data-gallery-main-next], [data-gallery-main-prev], [data-thumbs-next], [data-thumbs-prev], [data-gallery-thumb], [data-modal-thumb], [data-gallery-zoom], [data-gallery-model-ar], [data-inline-model-ar]');
      if (galleryControl) {
        const imageAlt = clean(galleryControl.dataset.imageAlt || galleryControl.dataset.alt || galleryControl.getAttribute('aria-label'));
        publish('product_gallery_click', productPayload(productRoot, {
          event_label: imageAlt || `Gallery interaction: ${interactionType(galleryControl)}`,
          gallery_action: interactionType(galleryControl),
          image_name: imageAlt,
          media_id: clean(galleryControl.dataset.mediaId),
          image_position: visiblePosition(galleryControl, '[data-gallery-thumb], [data-modal-thumb]')
        }));
      }

      const wishlist = event.target.closest('[data-wishlist-btn], [data-wishlist-btn-secondary], [data-wishlist-header]');
      if (wishlist) {
        const willRemove = wishlist.getAttribute('aria-pressed') === 'true' || wishlist.classList.contains('is-active');
        publish('product_wishlist_click', productPayload(productRoot, {
          event_label: `${willRemove ? 'Removed from' : 'Added to'} wishlist: ${clean(productRoot.dataset.productTitle)}`,
          wishlist_action: willRemove ? 'remove' : 'add',
          element_name: elementLabel(wishlist)
        }));
      }

      const checkoutCta = event.target.closest('[data-add-to-cart], [data-sticky-atc-submit]');
      if (checkoutCta) {
        const source = checkoutCta.matches('[data-sticky-atc-submit]') ? 'sticky_bar' : 'main_cta';
        publish('product_checkout_cta_click', productPayload(productRoot, {
          event_label: `Checkout CTA clicked: ${source}`,
          cta_source: source,
          cta_text: elementLabel(checkoutCta)
        }));
      }

      const dynamicPayment = event.target.closest('.shopify-payment-button button, .shopify-payment-button__button, shopify-accelerated-checkout, .pdp-dynamic-checkout button');
      if (dynamicPayment) {
        publish('product_express_payment_click', productPayload(productRoot, {
          event_label: `Express payment clicked: ${elementLabel(dynamicPayment) || 'Shopify dynamic checkout'}`,
          payment_option: elementLabel(dynamicPayment) || 'Shopify dynamic checkout'
        }));
      }

      const serviceControl = event.target.closest('[data-service-toggle], [data-service-option], [data-service-close]');
      if (serviceControl) {
        const serviceTitle = clean(serviceControl.dataset.serviceTitle || serviceControl.querySelector('[data-service-title]')?.textContent || elementLabel(serviceControl));
        publish('product_service_click', productPayload(productRoot, {
          event_label: `Service clicked: ${serviceTitle}`,
          service_name: serviceTitle,
          service_price: number(serviceControl.dataset.servicePrice),
          service_action: serviceControl.matches('[data-service-toggle]') ? 'open_drawer' : serviceControl.matches('[data-service-close]') ? 'close_drawer' : 'toggle_service'
        }));
      }

      const offerElement = event.target.closest('.promo-price, .pdp-warranty-note, .selected-bed-summary__bonus, .sticky-with-code, .ann-code');
      if (offerElement) {
        publish('product_offer_click', productPayload(productRoot, {
          event_label: `Offer clicked: ${elementLabel(offerElement)}`,
          offer_name: '£150 10-year warranty upgrade',
          element_name: elementLabel(offerElement)
        }));
      }

      const swatchesLink = event.target.closest('.btn-swatches, [href*="swatch"], [href*="order-swatches"]');
      if (swatchesLink) {
        const configuration = productConfiguration(productRoot);
        const fabricKey = Object.keys(configuration).find((key) => /fabric|colou?r/i.test(key));
        publish('product_free_swatches_click', productPayload(productRoot, {
          event_label: `Free swatches clicked: ${fabricKey ? configuration[fabricKey] : clean(productRoot.dataset.productTitle)}`,
          selected_fabric: fabricKey ? configuration[fabricKey] : '',
          destination: destination(swatchesLink)
        }));
      }

      const dimensionLink = event.target.closest('[href="#product-dimensions"], [href*="#product-dimensions"]');
      if (dimensionLink) {
        publish('product_dimensions_click', productPayload(productRoot, {
          event_label: 'View dimensions clicked',
          element_name: elementLabel(dimensionLink)
        }));
      }

      const reviewControl = event.target.closest('.reviews-nav, .reviews-carousel-dot, .rev-card, .rev-tag');
      if (reviewControl) {
        publish('product_reviews_click', productPayload(productRoot, {
          event_label: `Reviews interaction: ${elementLabel(reviewControl)}`,
          reviews_action: interactionType(reviewControl),
          element_name: elementLabel(reviewControl),
          element_position: visiblePosition(reviewControl, '.rev-card, .reviews-carousel-dot')
        }));
      }
    }

    const option = event.target.closest('[data-option-index][data-option-value]');
    if (productRoot && option && !option.disabled) {
      rememberOptionFocus(option);
      window.setTimeout(restoreOptionFocus, 0);
      window.setTimeout(restoreOptionFocus, 450);
      const index = clean(option.dataset.optionIndex);
      const oldValue = clean(productRoot.querySelector(`[data-tab-current][data-option-index="${index}"]`)?.textContent);
      const newValue = clean(option.dataset.optionValue);
      if (newValue && newValue !== oldValue) {
        const current = selectedVariant(productRoot);
        const nextPrice = number(option.dataset.optionPrice);
        publish('cro_option_select', {
          product: clean(productRoot.dataset.productTitle),
          option_name: optionName(option),
          old_value: oldValue,
          new_value: newValue,
          price_delta: nextPrice ? nextPrice - number(current.price) : 0
        });
        publish('product_option_select', productPayload(productRoot, {
          event_label: `${optionName(option)} selected: ${newValue}`,
          option_name: optionName(option),
          option_value: newValue,
          previous_value: oldValue,
          price_delta: nextPrice ? nextPrice - number(current.price) : 0,
          option_position: visiblePosition(option, '[data-option-index][data-option-value]')
        }));
      }
    }

    const swatches = event.target.closest('.btn-swatches, [href*="swatch"]');
    if (swatches && productRoot) {
      const configuration = productConfiguration(productRoot);
      const fabricKey = Object.keys(configuration).find((key) => /fabric|colou?r/i.test(key));
      publish('cro_swatches_click', {
        product: clean(productRoot.dataset.productTitle),
        selected_fabric_family: fabricKey ? configuration[fabricKey] : ''
      });
    }

    if (event.target.closest('[data-sticky-atc-submit]')) addToCartSource = 'sticky';
    if (event.target.closest('[data-add-to-cart]')) addToCartSource = 'main';

    const cartRoot = event.target.closest('[data-cart-page]');
    const cartItem = event.target.closest('[data-cart-item]');
    const quantityButton = event.target.closest('[data-qty-btn]');
    const removeButton = event.target.closest('.cart-item-remove, [data-bundle-child-remove]');
    if (cartRoot && (quantityButton || removeButton)) {
      publish('cro_cart_edit', {
        product: clean(cartItem?.dataset.productTitle),
        product_id: clean(cartItem?.dataset.productId),
        edited_option: removeButton ? 'remove' : 'quantity',
        source: quantityButton ? 'quantity_stepper' : 'remove_control'
      });
      if (removeButton?.dataset.bundleRole === 'service') {
        publish('cro_service_remove', {
          service: clean(removeButton.dataset.serviceTitle),
          price: number(removeButton.dataset.servicePrice)
        });
      }
    }

    const service = event.target.closest('[data-service-option]');
    if (service && !service.disabled) {
      const isSelected = service.getAttribute('aria-pressed') === 'true' || service.classList.contains('is-selected');
      publish(isSelected ? 'cro_service_remove' : 'cro_service_add', {
        service: clean(service.dataset.serviceTitle),
        price: number(service.dataset.servicePrice)
      });
    }

    const checkout = event.target.closest('[name="checkout"]');
    if (checkout && cartRoot) {
      publish('cro_checkout_click', {
        cart_total: number(cartRoot.dataset.cartTotal),
        discount: clean(cartRoot.dataset.cartDiscountState) || 'none',
        delivery_promise: clean(cartRoot.dataset.deliveryPromise),
        source: clean(checkout.dataset.checkoutSource) || 'main'
      });
    }

    const filter = event.target.closest('.filter-option[href], .active-filter-tag[href], .price-clear[href], .filters-clear[href]');
    const collectionRoot = filter
      ? (event.target.closest('[data-collection-page]') || document.querySelector('[data-collection-page]'))
      : null;
    if (collectionRoot && filter) {
      publish('cro_collection_filter', {
        value: clean(filter.querySelector('.filter-option-label')?.textContent || filter.textContent),
        result_count: number(filter.querySelector('.filter-option-count')?.textContent || collectionRoot.dataset.resultCount)
      });
    }
  }, true);

  document.addEventListener('change', (event) => {
    const sort = event.target.closest('.sort-select');
    const collectionRoot = sort?.closest('[data-collection-page]');
    if (sort && collectionRoot) {
      publish('cro_collection_sort', {
        value: clean(sort.value),
        result_count: number(collectionRoot.dataset.resultCount)
      });
    }

    const quantity = event.target.closest('.cart-qty-input');
    const cartRoot = quantity?.closest('[data-cart-page]');
    const cartItem = quantity?.closest('[data-cart-item]');
    if (quantity && cartRoot && !event.isTrusted) return;
    if (quantity && cartRoot) {
      publish('cro_cart_edit', {
        product: clean(cartItem?.dataset.productTitle),
        product_id: clean(cartItem?.dataset.productId),
        edited_option: 'quantity',
        new_value: number(quantity.value),
        source: 'quantity_input'
      });
    }
  }, true);

  document.addEventListener('submit', (event) => {
    const priceForm = event.target.closest('.price-range');
    const collectionRoot = priceForm ? document.querySelector('[data-collection-page]') : null;
    if (priceForm && collectionRoot) {
      const minimum = clean(priceForm.querySelector('input[aria-label*="Minimum"]')?.value) || '0';
      const maximum = clean(priceForm.querySelector('input[aria-label*="Maximum"]')?.value) || 'any';
      publish('cro_collection_filter', {
        value: `price:${minimum}-${maximum}`,
        result_count: number(collectionRoot.dataset.resultCount)
      });
    }

    const form = event.target.closest('[data-product-form]');
    const root = form?.closest('[data-product-section]');
    if (!form || !root || (form.checkValidity && !form.checkValidity())) return;
    const variant = selectedVariant(root);
    publish('cro_checkout_submit', {
      product: { id: clean(root.dataset.productId), title: clean(root.dataset.productTitle) },
      variant_id: clean(variant.id),
      configuration: productConfiguration(root),
      visible_price: number(variant.price || root.dataset.productPrice),
      source: addToCartSource
    });
    addToCartSource = 'main';
  }, true);

  document.addEventListener('variant:change', () => {
    const root = document.querySelector('[data-product-section]');
    if (!root) return;
    window.setTimeout(() => {
      const variant = selectedVariant(root);
      root.dataset.variantId = clean(variant.id);
      root.dataset.productPrice = String(number(variant.price));
      const configuration = productConfiguration(root);
      const selection = Object.entries(configuration).map(([key, value]) => `${key}: ${value}`).join('. ');
      announce(root, `${selection}. Price updated.`);
      restoreOptionFocus();
    }, 0);
    window.setTimeout(restoreOptionFocus, 450);
  });

  function interactionType(element) {
    if (element.matches('[data-add-to-cart], [data-sticky-atc-submit]')) return 'checkout_button';
    if (element.matches('[data-wishlist-btn], [data-wishlist-btn-secondary], [data-wishlist-header]')) {
      return element.getAttribute('aria-pressed') === 'true' || element.classList.contains('is-active')
        ? 'wishlist_remove'
        : 'wishlist_add';
    }
    if (element.matches('[data-gallery-main-next], [data-thumbs-next], [data-modal-next]')) return 'gallery_next';
    if (element.matches('[data-gallery-main-prev], [data-thumbs-prev], [data-modal-prev]')) return 'gallery_previous';
    if (element.matches('[data-gallery-thumb], [data-modal-thumb]')) return 'gallery_thumbnail';
    if (element.matches('[data-variant-tab]')) return 'option_tab';
    if (element.matches('.color-opt, [data-color-option]')) return 'colour_swatch';
    if (element.matches('[data-storage-option]')) return 'storage_option';
    if (element.matches('[data-headboard-option]')) return 'headboard_option';
    if (element.matches('[data-service-option], [data-service-toggle]')) return 'delivery_service';
    if (element.matches('[data-mattress-option-card], [data-mattress-toggle]')) return 'mattress_option';
    if (element.matches('[data-option-index][data-option-value]')) return 'product_option';
    if (element.matches('[data-mobile-toggle], [data-mobile-close]')) return 'mobile_menu';
    if (element.matches('[data-header-search-toggle], [type="search"]')) return 'site_search';
    if (element.matches('[data-faq-trigger], summary, [aria-expanded]')) return 'accordion_toggle';
    if (element.matches('[data-de-check]')) return 'delivery_postcode_check';
    if (element.closest('.pcard')) return 'product_card';
    const href = clean(element.getAttribute('href'));
    if (href.startsWith('tel:')) return 'phone_link';
    if (href.startsWith('mailto:')) return 'email_link';
    if (href) {
      try { if (new URL(href, location.href).origin !== location.origin) return 'outbound_link'; } catch (error) {}
      if (element.closest('header, .hdr-menu, #mobile-drawer')) return 'navigation_link';
      if (element.closest('footer')) return 'footer_link';
      return 'internal_link';
    }
    if (element.matches('button, [role="button"]')) return 'button';
    return 'control';
  }

  function elementLabel(element) {
    return compact(
      element.getAttribute('aria-label')
      || element.dataset.productTitle
      || element.dataset.optionValue
      || element.getAttribute('title')
      || element.querySelector('img[alt]')?.getAttribute('alt')
      || element.textContent
      || element.getAttribute('name')
      || element.id
      || element.tagName.toLowerCase()
    );
  }

  function sectionContext(element) {
    const section = element.closest('.shopify-section, section, header, footer, [role="dialog"]');
    if (!section) return { id: '', name: '', position: 0 };
    const id = compact((section.id || '').replace(/^shopify-section-/, ''));
    const heading = section.querySelector('h1, h2, h3, [aria-label]');
    const siblings = Array.from(document.querySelectorAll('header, main .shopify-section, main > section, footer'));
    return {
      id,
      name: compact(heading?.textContent || section.getAttribute('aria-label') || id || section.className || section.tagName),
      position: Math.max(1, siblings.indexOf(section) + 1)
    };
  }

  function elementPosition(element) {
    const card = element.closest('.pcard');
    if (card) return Array.from(card.parentElement?.querySelectorAll(':scope > .pcard') || []).indexOf(card) + 1;
    const scope = element.closest('[role="tablist"], nav, .opts-grid, .product-color-panels, .gallery-thumbs, ul, ol');
    if (!scope) return 0;
    const controls = Array.from(scope.querySelectorAll('a[href], button, [role="tab"], [role="button"]'));
    return controls.indexOf(element) + 1;
  }

  function destination(element) {
    const href = clean(element.getAttribute('href'));
    if (!href) return '';
    try {
      const url = new URL(href, location.href);
      return url.origin === location.origin ? url.pathname : `${url.origin}${url.pathname}`;
    } catch (error) {
      return compact(href);
    }
  }

  function productContext(element) {
    const card = element.closest('.pcard');
    const product = element.closest('[data-product-section]');
    return {
      product_id: compact(element.dataset.productId || card?.dataset.productId || product?.dataset.productId),
      product_name: compact(element.dataset.productTitle || card?.querySelector('.pcard-title')?.textContent || product?.dataset.productTitle),
      variant_id: compact(product?.querySelector('[data-product-form] input[name="id"]')?.value || product?.dataset.variantId)
    };
  }

  function initGlobalInteractions() {
    document.addEventListener('click', (event) => {
      if (!(event.target instanceof Element)) return;
      const element = event.target.closest('a[href], button, summary, [role="button"], [role="tab"], input[type="submit"], input[type="button"]');
      if (!element || element.disabled || element.getAttribute('aria-disabled') === 'true') return;
      const section = sectionContext(element);
      publish('cro_interaction', Object.assign({
        interaction_type: interactionType(element),
        element_name: elementLabel(element),
        element_id: compact(element.id),
        element_position: elementPosition(element),
        section_id: section.id,
        section_name: section.name,
        section_position: section.position,
        destination: destination(element)
      }, productContext(element)));
    }, true);

    document.addEventListener('change', (event) => {
      if (!(event.target instanceof Element)) return;
      const control = event.target.closest('select, input[type="checkbox"], input[type="radio"]');
      if (!control) return;
      const section = sectionContext(control);
      const selectedText = control instanceof HTMLSelectElement
        ? compact(control.selectedOptions?.[0]?.textContent)
        : (control.checked ? 'selected' : 'not_selected');
      publish('cro_control_change', {
        control_name: compact(control.getAttribute('aria-label') || control.name || control.id || control.type),
        control_type: compact(control.tagName === 'SELECT' ? 'select' : control.type),
        selected_state: selectedText,
        section_id: section.id,
        section_name: section.name
      });
    }, true);

    document.addEventListener('submit', (event) => {
      if (!(event.target instanceof HTMLFormElement)) return;
      const form = event.target;
      const section = sectionContext(form);
      publish('cro_form_submit', {
        form_name: compact(form.getAttribute('aria-label') || form.id || form.getAttribute('name') || form.action.split('/').pop()),
        form_action: compact(new URL(form.action || location.href, location.href).pathname),
        field_count: form.querySelectorAll('input, select, textarea').length,
        section_id: section.id,
        section_name: section.name
      });
    }, true);

    document.addEventListener('play', (event) => {
      if (!(event.target instanceof HTMLMediaElement)) return;
      publish('cro_media_engagement', {
        media_action: 'play',
        media_name: compact(event.target.getAttribute('aria-label') || event.target.title || event.target.currentSrc.split('/').pop())
      });
    }, true);

    document.addEventListener('pause', (event) => {
      if (!(event.target instanceof HTMLMediaElement) || event.target.ended) return;
      publish('cro_media_engagement', {
        media_action: 'pause',
        media_name: compact(event.target.getAttribute('aria-label') || event.target.title || event.target.currentSrc.split('/').pop()),
        media_percent: event.target.duration ? Math.round((event.target.currentTime / event.target.duration) * 100) : 0
      });
    }, true);

    document.addEventListener('ended', (event) => {
      if (!(event.target instanceof HTMLMediaElement)) return;
      publish('cro_media_engagement', {
        media_action: 'complete',
        media_name: compact(event.target.getAttribute('aria-label') || event.target.title || event.target.currentSrc.split('/').pop()),
        media_percent: 100
      });
    }, true);
  }

  function componentName(element) {
    if (element.matches('.product-color-options')) return 'Colour selection';
    if (element.matches('.variant-tabs-wrap')) return 'Product configuration';
    if (element.matches('.pdp-payment-methods')) return 'Payment methods';
    if (element.matches('[data-whats-in-box]')) return compact(element.querySelector('h2')?.textContent || 'What arrives with your order');
    if (element.matches('.reviews-list')) return compact(element.closest('section')?.querySelector('h2')?.textContent || 'Latest reviews');
    if (element.matches('.delivery-card')) return compact(element.querySelector('h2')?.textContent || 'Delivery timeline');
    if (element.matches('.related-products-section')) return compact(element.querySelector('h2')?.textContent || 'Related products');
    const heading = element.querySelector('h1, h2, h3, [aria-label]') || element.closest('section')?.querySelector('h1, h2, h3');
    const id = clean(element.id).replace(/^shopify-section-/, '');
    return compact(heading?.textContent || element.getAttribute('aria-label') || id.split('__').pop() || element.className || element.tagName);
  }

  function initComponentViews() {
    const selector = [
      'header',
      'main .shopify-section',
      'main > section',
      'footer',
      '[role="dialog"]',
      '.product-color-options',
      '.variant-tabs-wrap',
      '.pdp-payment-methods',
      '[data-whats-in-box]',
      '.reviews-list',
      '.delivery-card',
      '.related-products-section'
    ].join(',');
    const components = Array.from(new Set(document.querySelectorAll(selector)));
    const isVisible = (component) => {
      if (component.hidden || component.getAttribute('aria-hidden') === 'true') return false;
      const styles = getComputedStyle(component);
      if (styles.display === 'none' || styles.visibility === 'hidden' || Number(styles.opacity) === 0) return false;
      const rect = component.getBoundingClientRect();
      return rect.width > 4 && rect.height > 4;
    };
    const sendView = (component) => {
      if (!isVisible(component)) return false;
      const section = sectionContext(component);
      const name = componentName(component);
      publish('cro_component_view', {
        component_name: name,
        component_id: compact(component.id),
        component_position: Math.max(1, components.indexOf(component) + 1),
        section_id: section.id,
        section_name: section.name
      }, `component:${location.pathname}:${component.id || name}:${components.indexOf(component)}`);
      return true;
    };
    if (!('IntersectionObserver' in window)) {
      components.forEach(sendView);
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting || !sendView(entry.target)) return;
        observer.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -35% 0px', threshold: 0 });
    components.forEach((component) => observer.observe(component));

    const visibilityObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        const component = mutation.target;
        if (!components.includes(component) || !isVisible(component)) return;
        const rect = component.getBoundingClientRect();
        if (rect.bottom <= 0 || rect.top >= innerHeight) return;
        if (sendView(component)) observer.unobserve(component);
      });
    });
    components.forEach((component) => {
      visibilityObserver.observe(component, { attributes: true, attributeFilter: ['class', 'hidden', 'aria-hidden', 'style'] });
    });
  }

  function initScrollDepth() {
    const thresholds = [25, 50, 75, 90];
    let ticking = false;
    const measure = () => {
      ticking = false;
      const scrollable = Math.max(1, document.documentElement.scrollHeight - innerHeight);
      const depth = Math.min(100, Math.round((scrollY / scrollable) * 100));
      thresholds.forEach((threshold) => {
        if (depth >= threshold) publish('cro_scroll_depth', { scroll_percent: threshold }, `scroll:${location.pathname}:${threshold}`);
      });
    };
    addEventListener('scroll', () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(measure);
    }, { passive: true });
    measure();
  }

  function initCroTracking() {
    initGlobalInteractions();
    initComponentViews();
    initScrollDepth();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initPageViews();
      protectStickyFocus();
      initCroTracking();
    }, { once: true });
  } else {
    initPageViews();
    protectStickyFocus();
    initCroTracking();
  }
})();
